import React from "react";

// Minimalist original line-art icons for the shirt configurator.
// All use stroke="currentColor" so colour follows the parent (selected/hover states).
const base = { viewBox: "0 0 64 76", fill: "none", stroke: "currentColor", strokeWidth: 1.4, strokeLinecap: "round", strokeLinejoin: "round" };

// ---------- Fit ----------
export function FitIcon({ variant }) {
  const sides = {
    slim: "M22 10 L18 70 M42 10 L46 70",
    structured: "M20 10 L17 70 M44 10 L47 70",
    relaxed: "M19 10 L14 70 M45 10 L50 70",
  }[variant];
  return (
    <svg {...base}>
      <path d="M22 10 L14 16 L18 24 L22 18" />
      <path d="M42 10 L50 16 L46 24 L42 18" />
      <path d="M26 6 L22 10 L32 16 L42 10 L38 6" />
      <path d={sides} />
      <path d={variant === "slim" ? "M18 70 L46 70" : variant === "relaxed" ? "M14 70 L50 70" : "M17 70 L47 70"} />
    </svg>
  );
}

// ---------- Sleeves ----------
export function SleeveIcon({ variant }) {
  const long = variant === "long";
  return (
    <svg {...base}>
      <path d="M26 6 L22 10 L32 16 L42 10 L38 6" />
      <path d="M22 10 L18 70 L46 70 L42 10" />
      <path d={long ? "M18 14 L6 44 L12 50 L22 20" : "M18 14 L10 30 L16 34 L22 20"} />
      <path d={long ? "M42 14 L54 44 L48 50 L38 20" : "M42 14 L50 30 L44 34 L38 20"} />
    </svg>
  );
}

// ---------- Pocket ----------
export function PocketIcon({ variant }) {
  if (variant === "none") {
    return (
      <svg {...base}>
        <circle cx="32" cy="38" r="20" />
        <path d="M18 24 L46 52" />
      </svg>
    );
  }
  const flap = variant.includes("flap");
  const double = variant.includes("double");
  const Pocket = ({ x }) => (
    <g>
      <rect x={x} y="30" width="14" height="16" />
      {flap && <path d={`M${x} 30 L${x + 7} 24 L${x + 14} 30`} />}
    </g>
  );
  return (
    <svg {...base}>
      {double ? (
        <>
          <Pocket x={10} />
          <Pocket x={40} />
        </>
      ) : (
        <Pocket x={25} />
      )}
    </svg>
  );
}

// ---------- Collar ----------
export function CollarIcon({ variant }) {
  switch (variant) {
    case "chinese":
      return (
        <svg {...base}>
          <path d="M14 26 Q32 14 50 26" />
          <path d="M14 26 L14 34 M50 26 L50 34" />
          <circle cx="32" cy="30" r="1.4" fill="currentColor" />
        </svg>
      );
    case "button-down":
      return (
        <svg {...base}>
          <path d="M32 14 L14 24 L22 40 L32 26 L42 40 L50 24 Z" />
          <circle cx="20" cy="34" r="1.3" fill="currentColor" />
          <circle cx="44" cy="34" r="1.3" fill="currentColor" />
          <circle cx="32" cy="30" r="1.3" fill="currentColor" />
        </svg>
      );
    case "cutaway":
      return (
        <svg {...base}>
          <path d="M32 16 L10 20 L22 40 L32 24 L42 40 L54 20 Z" />
          <circle cx="32" cy="28" r="1.3" fill="currentColor" />
        </svg>
      );
    case "peak-lapel":
      return (
        <svg {...base}>
          <path d="M32 12 L12 22 L26 46 L32 30 L38 46 L52 22 Z" />
        </svg>
      );
    default: // standard
      return (
        <svg {...base}>
          <path d="M32 14 L16 22 L23 38 L32 25 L41 38 L48 22 Z" />
          <circle cx="32" cy="29" r="1.3" fill="currentColor" />
        </svg>
      );
  }
}

// ---------- Placket ----------
export function PlacketIcon({ variant }) {
  const withPlacket = variant === "with";
  return (
    <svg {...base}>
      <path d="M26 6 L22 10 L32 16 L42 10 L38 6" />
      <path d="M22 10 L18 70 M42 10 L46 70" />
      {withPlacket && <path d="M28 16 L26 68 M36 16 L38 68" />}
      {[22, 32, 42, 52, 62].map((y) => (
        <circle key={y} cx="32" cy={y} r="1.3" fill="currentColor" />
      ))}
    </svg>
  );
}

// ---------- Bottom cut ----------
export function BottomCutIcon({ variant }) {
  const round = variant === "round";
  return (
    <svg {...base}>
      <path d="M18 10 L16 60 M46 10 L48 60" />
      <path d={round ? "M16 60 Q20 68 26 60" : "M16 60 L26 60"} />
      <path d={round ? "M48 60 Q44 68 38 60" : "M48 60 L38 60"} />
      <path d="M26 60 L26 66 M38 60 L38 66" strokeOpacity="0" />
      <circle cx="32" cy="20" r="1.3" fill="currentColor" />
      <circle cx="32" cy="34" r="1.3" fill="currentColor" />
      <circle cx="32" cy="48" r="1.3" fill="currentColor" />
    </svg>
  );
}

// ---------- Cuff ----------
export function CuffIcon({ variant }) {
  const french = variant === "french-fold";
  return (
    <svg {...base}>
      <path d="M14 12 L22 44 L46 44 L50 12" />
      {french ? (
        <>
          <rect x="18" y="44" width="30" height="10" />
          <rect x="21" y="54" width="24" height="8" />
          <circle cx="33" cy="58" r="1.5" fill="currentColor" />
        </>
      ) : (
        <>
          <rect x="20" y="44" width="26" height="14" />
          <circle cx="33" cy="51" r="1.5" fill="currentColor" />
        </>
      )}
    </svg>
  );
}

// ---------- Shirt type ----------
export function ShirtTypeIcon({ variant }) {
  return (
    <svg {...base}>
      <path d="M26 6 L14 14 L20 24 L26 18 L26 70 L38 70 L38 18 L44 24 L50 14 L38 6" />
      {variant === "tuxedo" && (
        <>
          <path d="M30 16 L28 70 M34 16 L36 70" strokeDasharray="2 3" />
        </>
      )}
      {variant === "hunter" && (
        <>
          <rect x="18" y="28" width="10" height="12" />
          <path d="M18 28 L23 23 L28 28" />
          <rect x="36" y="28" width="10" height="12" />
          <path d="M36 28 L41 23 L46 28" />
        </>
      )}
      {[24, 34, 44, 54].map((y) => (
        <circle key={y} cx="32" cy={y} r="1.2" fill="currentColor" />
      ))}
    </svg>
  );
}

// ---------- Upper body shape ----------
export function UpperBodyIcon({ variant }) {
  const paths = {
    athletic: "M20 8 Q32 4 44 8 L48 40 Q32 50 16 40 Z",
    average: "M20 10 Q32 6 44 10 L46 40 Q32 46 18 40 Z",
    healthy: "M18 10 Q32 6 46 10 L50 42 Q32 50 14 42 Z",
  };
  return (
    <svg {...base}>
      <circle cx="32" cy="4" r="0" />
      <path d={paths[variant]} />
    </svg>
  );
}

// ---------- Lower body / hip shape ----------
export function HipShapeIcon({ variant }) {
  const curve = {
    flat: "M26 4 L24 60",
    standard: "M26 4 Q22 32 26 60",
    heavy: "M26 4 Q18 32 27 60",
  }[variant];
  return (
    <svg {...base}>
      <path d="M26 4 L24 60" strokeDasharray="2 3" opacity="0.5" />
      <path d={curve} />
      <rect x="14" y="8" width="14" height="8" rx="1" />
    </svg>
  );
}

// ---------- Shoulder type ----------
export function ShoulderTypeIcon({ variant }) {
  const sloping = variant === "sloping";
  return (
    <svg {...base}>
      <circle cx="32" cy="10" r="7" />
      <path d={sloping ? "M12 30 Q32 18 52 30" : "M12 24 L52 24"} strokeDasharray={sloping ? "0" : "2 3"} opacity={sloping ? 1 : 0.5} />
      <path d={sloping ? "M12 30 L12 46 M52 30 L52 46" : "M12 24 L12 46 M52 24 L52 46"} />
      <path d="M16 46 L48 46" />
    </svg>
  );
}
